/**
 * Profile Page
 * User account settings and profile management
 * For helpers: includes photo, service areas, bio, documents
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, helpersAPI, portsAPI, ratingsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  User,
  Mail,
  Phone,
  Save,
  Lock,
  Shield,
  Bell,
  Star,
  MapPin,
  Search,
  Plus,
  X,
  Upload,
  FileText,
  Trash2,
  File,
  Camera,
  Edit3,
  Globe,
  ChevronDown,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { showToast } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const isHelper = user?.user_type === 'helper';

  // Helper-specific state
  const [helperLoading, setHelperLoading] = useState(isHelper);
  const [helperSaving, setHelperSaving] = useState(false);
  const [helperFormData, setHelperFormData] = useState({
    bio: '',
    experience_years: 0,
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [portSearch, setPortSearch] = useState('');
  const [portResults, setPortResults] = useState([]);
  const [searchingPorts, setSearchingPorts] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Reviews state
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [loadingReceivedReviews, setLoadingReceivedReviews] = useState(false);
  const [sentReviews, setSentReviews] = useState([]);
  const [loadingSentReviews, setLoadingSentReviews] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewData, setEditReviewData] = useState({ rating: 0, comment: '' });

  // Languages state
  const [languages, setLanguages] = useState([]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  const availableLanguages = [
    'English', 'French', 'Spanish', 'Italian', 'German', 'Portuguese',
    'Greek', 'Turkish', 'Arabic', 'Russian', 'Mandarin', 'Japanese',
    'Dutch', 'Croatian', 'Swedish', 'Norwegian', 'Danish'
  ];

  const documentTypes = [
    { value: 'cv', label: 'CV / Resume' },
    { value: 'reference', label: 'Reference Letter' },
    { value: 'recommendation', label: 'Recommendation' },
  ];

  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        let phone = user.phone || '';

        if (isHelper && !phone) {
          try {
            const helperData = await helpersAPI.getMyProfile();
            if (helperData.profile?.phone) {
              phone = helperData.profile.phone;
            }
          } catch (e) {
            // Ignore
          }
        }

        setFormData({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          phone: phone,
        });
      }
    };
    loadProfile();
  }, [user]);

  // Load profile photo for all users
  useEffect(() => {
    if (user?.profile_photo_url) {
      setProfilePhoto(getFullPhotoUrl(user.profile_photo_url));
    }
  }, [user?.profile_photo_url]);

  // Load helper-specific data
  useEffect(() => {
    if (!isHelper) return;

    const loadHelperData = async () => {
      try {
        const [profileData, areasData, docsData] = await Promise.all([
          helpersAPI.getMyProfile(),
          helpersAPI.getServiceAreas(),
          helpersAPI.getDocuments(),
        ]);

        const rawPhotoUrl = profileData.profile?.profile_photo_url || null;
        if (rawPhotoUrl) setProfilePhoto(getFullPhotoUrl(rawPhotoUrl));
        setServiceAreas(areasData.service_areas || []);
        setDocuments(docsData.data || []);

        if (profileData.profile) {
          setHelperFormData({
            bio: profileData.profile.bio || '',
            experience_years: profileData.profile.experience_years || 0,
          });
          setLanguages(profileData.profile.languages || []);
        }
      } catch (error) {
        console.error('Failed to load helper data:', error);
      } finally {
        setHelperLoading(false);
      }
    };
    loadHelperData();
  }, [isHelper]);

  // Load reviews (received + sent)
  useEffect(() => {
    const loadReviews = async () => {
      setLoadingReceivedReviews(true);
      setLoadingSentReviews(true);
      try {
        const [receivedRes, sentRes] = await Promise.all([
          usersAPI.getMyReviews(),
          usersAPI.getMySentReviews(),
        ]);
        setReceivedReviews(receivedRes.data || []);
        setSentReviews(sentRes.data || []);
      } catch (error) {
        console.error('Failed to load reviews:', error);
      } finally {
        setLoadingReceivedReviews(false);
        setLoadingSentReviews(false);
      }
    };
    loadReviews();
  }, []);

  // Port search debounce
  useEffect(() => {
    if (!isHelper) return;

    const searchPorts = async () => {
      if (portSearch.length < 2) {
        setPortResults([]);
        return;
      }
      setSearchingPorts(true);
      try {
        const data = await portsAPI.search(portSearch);
        const addedPortIds = new Set(serviceAreas.map(a => a.port_id));
        setPortResults((data.ports || []).filter(p => !addedPortIds.has(p.id)));
      } catch (error) {
        console.error('Port search failed:', error);
      } finally {
        setSearchingPorts(false);
      }
    };

    const debounce = setTimeout(searchPorts, 300);
    return () => clearTimeout(debounce);
  }, [portSearch, serviceAreas, isHelper]);

  const getFullPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${UPLOADS_BASE_URL}${url}`;
  };

  // Basic profile handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await usersAPI.updateProfile(formData);
      const updatedUser = response.data || response.user || response;
      updateUser(updatedUser);
      showToast('Profile updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper profile handlers
  const handleSaveHelperProfile = async () => {
    setHelperSaving(true);
    try {
      await helpersAPI.updateProfile(helperFormData);
      showToast('Helper profile updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update helper profile', 'error');
    } finally {
      setHelperSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const result = await usersAPI.uploadProfilePhoto(file);
      const photoUrl = result.url || result.profile_photo_url;
      const fullUrl = getFullPhotoUrl(photoUrl);
      setProfilePhoto(fullUrl);
      updateUser({ profile_photo_url: photoUrl });
      showToast('Profile photo updated', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleAddPort = async (port) => {
    try {
      const newArea = await helpersAPI.addServiceArea(port.id, port.name);
      setServiceAreas(prev => [...prev, { id: newArea.id, port_id: port.id, port_name: port.name }]);
      setPortSearch('');
      setPortResults([]);
      showToast(`Added ${port.name} to service areas`, 'success');
    } catch (error) {
      showToast(error.message || 'Failed to add service area', 'error');
    }
  };

  const handleRemovePort = async (areaId) => {
    try {
      await helpersAPI.removeServiceArea(areaId);
      setServiceAreas(prev => prev.filter(a => a.id !== areaId));
      showToast('Service area removed', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to remove service area', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const docType = e.target.dataset.doctype || 'other';
    setUploading(true);
    try {
      const document = await helpersAPI.uploadDocument(file, docType, '');
      setDocuments(prev => [document, ...prev]);
      showToast('Document uploaded successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload document', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await helpersAPI.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      showToast('Document deleted', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete document', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Review handlers
  const handleEditReview = (review) => {
    setEditingReview(review);
    setEditReviewData({ rating: review.rating, comment: review.comment || '' });
  };

  const handleSaveReview = async () => {
    if (!editingReview) return;
    try {
      await ratingsAPI.update(editingReview.id, editReviewData);
      setSentReviews(prev => prev.map(r =>
        r.id === editingReview.id
          ? { ...r, rating: editReviewData.rating, comment: editReviewData.comment }
          : r
      ));
      setEditingReview(null);
      showToast('Review updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update review', 'error');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await ratingsAPI.delete(reviewId);
      setSentReviews(prev => prev.filter(r => r.id !== reviewId));
      showToast('Review deleted', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete review', 'error');
    }
  };

  // Language handlers
  const handleAddLanguage = async (lang) => {
    const updatedLanguages = [...languages, lang];
    setLanguages(updatedLanguages);
    setShowLanguageDropdown(false);
    try {
      await helpersAPI.updateProfile({ languages: updatedLanguages });
    } catch (error) {
      setLanguages(languages); // rollback
      showToast(error.message || 'Failed to add language', 'error');
    }
  };

  const handleRemoveLanguage = async (lang) => {
    const updatedLanguages = languages.filter(l => l !== lang);
    setLanguages(updatedLanguages);
    try {
      await helpersAPI.updateProfile({ languages: updatedLanguages });
    } catch (error) {
      setLanguages(languages); // rollback
      showToast(error.message || 'Failed to remove language', 'error');
    }
  };

  const getDocTypeLabel = (type) => {
    const docType = documentTypes.find(d => d.value === type);
    return docType ? docType.label : type;
  };

  // Show spinner while helper data loads
  if (isHelper && helperLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Edit My Profile
        </h1>
        <p className="text-navy-600 mt-1">
          Manage your account settings{isHelper ? ', skills, and documents' : ''}
        </p>
      </div>

      {/* Profile Photo */}
      <div className="card">
        <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Camera className="h-5 w-5 inline mr-2" />
          Profile Photo
        </h2>
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden border-4 border-primary-200">
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <User className="h-12 w-12 text-primary-400" />
            )}
          </div>
          <div>
            <label className="btn-secondary cursor-pointer inline-flex items-center">
              <input
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto}
              />
              {uploadingPhoto ? (
                <>
                  <span className="spinner h-4 w-4 mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Photo
                </>
              )}
            </label>
            <p className="text-sm text-navy-500 mt-2">JPG, PNG. Max 5MB.</p>
          </div>
        </div>
      </div>

      {/* Basic Profile Info */}
      <div className="card">
        <div className="flex items-center mb-6">
          <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
            {user?.profile_photo_url ? (
              <img
                src={user.profile_photo_url.startsWith('http') ? user.profile_photo_url : `${UPLOADS_BASE_URL}${user.profile_photo_url}`}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-primary-700 font-bold text-xl">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            )}
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-navy-900">
              {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-navy-500 capitalize">
              {user?.user_type?.replace('_', ' ')}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="+1 234 567 8900"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Helper: About You / CV */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <User className="h-5 w-5 inline mr-2" />
            About You (CV)
          </h2>
          <p className="text-navy-600 mb-4">
            Write a brief summary of your experience and qualifications.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-navy-700 mb-1">
              Years of Experience
            </label>
            <input
              type="number"
              min="0"
              max="50"
              value={helperFormData.experience_years}
              onChange={(e) => setHelperFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
              className="input-field w-32"
              placeholder="0"
            />
          </div>

          <textarea
            value={helperFormData.bio}
            onChange={(e) => setHelperFormData(prev => ({ ...prev, bio: e.target.value }))}
            rows={6}
            className="input-field"
            placeholder="Tell yacht owners about your experience, qualifications, certifications, and why they should work with you..."
          />

          <div className="pt-4">
            <button
              onClick={handleSaveHelperProfile}
              disabled={helperSaving}
              className="btn-primary"
            >
              {helperSaving ? (
                <span className="flex items-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Saving...
                </span>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Helper Details
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Helper: Service Areas */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <MapPin className="h-5 w-5 inline mr-2" />
            Service Areas
          </h2>
          <p className="text-navy-600 mb-4">
            Add ports where you're available to help. You'll receive notifications for new requests in these areas.
          </p>

          {/* Port Search */}
          <div className="relative mb-4">
            <input
              type="text"
              value={portSearch}
              onChange={(e) => setPortSearch(e.target.value)}
              className="input-field pl-10"
              placeholder="Search for a port to add..."
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <p className="text-xs text-red-500 mt-1">Start typing the port name and select from the dropdown for reliable results</p>

            {searchingPorts && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="spinner w-5 h-5" />
              </div>
            )}

            {portResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {portResults.map((port) => (
                  <button
                    key={port.id}
                    onClick={() => handleAddPort(port)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-navy-900">{port.name}</p>
                      <p className="text-sm text-navy-500">{port.country}</p>
                    </div>
                    <Plus className="h-5 w-5 text-primary-600" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Current Service Areas */}
          <div className="flex flex-wrap gap-2">
            {serviceAreas.length === 0 ? (
              <p className="text-navy-400 italic">No service areas added yet</p>
            ) : (
              serviceAreas.map((area) => (
                <span
                  key={area.id}
                  className="inline-flex items-center px-3 py-1.5 bg-primary-100 text-primary-800 rounded-full"
                >
                  <MapPin className="h-3.5 w-3.5 mr-1.5" />
                  {area.port_name}
                  <button
                    onClick={() => handleRemovePort(area.id)}
                    className="ml-2 p-0.5 hover:bg-primary-200 rounded-full"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Helper: Languages */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <Globe className="h-5 w-5 inline mr-2" />
            Languages
          </h2>
          <p className="text-navy-600 mb-4">
            Add the languages you speak to help yacht owners find you.
          </p>

          {/* Current Languages */}
          <div className="flex flex-wrap gap-2 mb-4">
            {languages.length === 0 ? (
              <p className="text-navy-400 italic">No languages added yet</p>
            ) : (
              languages.map((lang) => (
                <span
                  key={lang}
                  className="inline-flex items-center px-3 py-1.5 bg-primary-100 text-primary-800 rounded-full"
                >
                  <Globe className="h-3.5 w-3.5 mr-1.5" />
                  {lang}
                  <button
                    onClick={() => handleRemoveLanguage(lang)}
                    className="ml-2 p-0.5 hover:bg-primary-200 rounded-full"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))
            )}
          </div>

          {/* Add Language */}
          <div className="relative">
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="btn-secondary inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Language
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>

            {showLanguageDropdown && (
              <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto w-64">
                {availableLanguages
                  .filter(lang => !languages.includes(lang))
                  .map((lang) => (
                    <button
                      key={lang}
                      onClick={() => handleAddLanguage(lang)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-navy-900"
                    >
                      {lang}
                    </button>
                  ))}
                {availableLanguages.filter(lang => !languages.includes(lang)).length === 0 && (
                  <p className="px-4 py-3 text-navy-400 text-sm">All languages added</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Helper: Documents & References */}
      {isHelper && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-4">
            <FileText className="h-5 w-5 inline mr-2" />
            Documents & References
          </h2>
          <p className="text-navy-600 mb-6">
            Upload your CV and reference letters to build trust with yacht owners.
          </p>

          {/* Upload Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {documentTypes.map((docType) => (
              <label key={docType.value} className="relative cursor-pointer">
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  data-doctype={docType.value}
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors">
                  <Upload className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-sm font-medium text-navy-700">{docType.label}</span>
                </div>
              </label>
            ))}
          </div>

          {uploading && (
            <div className="flex items-center justify-center py-4 text-navy-600">
              <span className="spinner h-5 w-5 mr-2" />
              Uploading...
            </div>
          )}

          {/* Documents List */}
          {documents.length === 0 ? (
            <div className="text-center py-8 text-navy-400">
              <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No documents uploaded yet</p>
              <p className="text-sm mt-1">Upload your CV and reference letters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">
                        {doc.file_name}
                      </p>
                      <div className="flex items-center text-xs text-navy-500 mt-0.5">
                        <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 rounded mr-2">
                          {getDocTypeLabel(doc.document_type)}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span className="mx-1">&bull;</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-navy-500">
            Accepted formats: PDF, JPEG, PNG, DOC, DOCX. Maximum file size: 10MB.
          </p>
        </div>
      )}

      {/* Security */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Lock className="h-5 w-5 inline mr-2" />
          Security
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-navy-900">Password</p>
              <p className="text-sm text-navy-500">Last changed: Never</p>
            </div>
            <button className="btn-secondary py-2">
              Change Password
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-navy-900">Two-Factor Authentication</p>
              <p className="text-sm text-navy-500">Add an extra layer of security</p>
            </div>
            <button className="btn-secondary py-2">
              Enable
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Bell className="h-5 w-5 inline mr-2" />
          Notification Preferences
        </h3>

        <div className="space-y-3">
          {[
            { label: 'New request notifications', description: 'Get notified about new requests in your area' },
            { label: 'Message notifications', description: 'Receive alerts when you get new messages' },
            { label: 'Service updates', description: 'Updates about your confirmed services' },
            { label: 'Email digest', description: 'Weekly summary of activity' },
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3">
              <div>
                <p className="font-medium text-navy-900">{item.label}</p>
                <p className="text-sm text-navy-500">{item.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Ratings & Reviews */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-6">
          <Star className="h-5 w-5 inline mr-2" />
          Ratings & Reviews
        </h3>

        {/* Reviews I've Received */}
        <div className="mb-8">
          <h4 className="text-md font-semibold text-navy-800 mb-3">Reviews I've Received</h4>

          {loadingReceivedReviews ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner w-6 h-6" />
            </div>
          ) : receivedReviews.length === 0 ? (
            <div className="text-center py-6 text-navy-400">
              <p>No reviews received yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {receivedReviews.map((review, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-navy-900">
                      {review.reviewer_first_name} {review.reviewer_last_name}
                    </p>
                    <p className="text-xs text-navy-400">
                      {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  {review.comment && (
                    <p className="text-sm text-navy-600 mt-2">"{review.comment}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="border-gray-200 mb-8" />

        {/* Reviews I've Given */}
        <div>
          <h4 className="text-md font-semibold text-navy-800 mb-3">Reviews I've Given</h4>

          {loadingSentReviews ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner w-6 h-6" />
            </div>
          ) : sentReviews.length === 0 ? (
            <div className="text-center py-6 text-navy-400">
              <p>You haven't written any reviews yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sentReviews.map((review) => (
                <div
                  key={review.id}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-navy-900">
                          {review.first_name} {review.last_name}
                        </p>
                        <p className="text-xs text-navy-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center mt-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      {review.comment && (
                        <p className="text-sm text-navy-600 mt-2">"{review.comment}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                      <button
                        onClick={() => handleEditReview(review)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Edit review"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete review"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Review Modal */}
      {editingReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-navy-900">
                Edit Review
              </h3>
              <button
                onClick={() => setEditingReview(null)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-navy-500" />
              </button>
            </div>

            <p className="text-sm text-navy-600 mb-4">
              Review for <span className="font-medium">{editingReview.first_name} {editingReview.last_name}</span>
            </p>

            {/* Star Picker */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-navy-700 mb-2">Rating</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setEditReviewData(prev => ({ ...prev, rating: s }))}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star
                      className={`h-7 w-7 ${s <= editReviewData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">Comment</label>
              <textarea
                value={editReviewData.comment}
                onChange={(e) => setEditReviewData(prev => ({ ...prev, comment: e.target.value }))}
                rows={4}
                className="input-field"
                placeholder="Update your review..."
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditingReview(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReview}
                disabled={editReviewData.rating === 0}
                className="btn-primary"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status */}
      <div className="card">
        <h3 className="text-lg font-display font-semibold text-navy-900 mb-4">
          <Shield className="h-5 w-5 inline mr-2" />
          Verification Status
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <Mail className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900">Email Verified</p>
                <p className="text-sm text-navy-500">{user?.email}</p>
              </div>
            </div>
            <span className="badge badge-verified">Verified</span>
          </div>

          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                <Phone className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900">Phone Number</p>
                <p className="text-sm text-navy-500">{user?.phone || 'Not added'}</p>
              </div>
            </div>
            {user?.phone ? (
              <span className="badge badge-verified">Verified</span>
            ) : (
              <button className="text-primary-600 text-sm font-medium">Add</button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
